// Prices page component
export function EPCInfoPage() {
    return `
<div class="min-h-screen bg-gray-50">
    <div class="hero">
        <div class="hero-content">
            <h1 class="hero-title">Information</h1>
            <p class="hero-subtitle">About energy certificates in Spain</p>
        </div>
    </div>

    <section class="section bg-gray-50">
        <div class="container">
            <div class="md:flex md:items-start md:space-x-8">

                <main class="md:w-2/3">

                    <div class="max-w-4xl mx-auto prose prose-lg">
                        <div class="bg-white p-8 rounded-lg shadow-sm">

                            <h2 id="why-energy-label" class="text-2xl text-blue-900 mb-4">Why an energy certificate?</h2>

                            <p class="mb-4">
The energy certificate, as we know it today, emerged from growing concern about climate change and the need to reduce the energy consumption of buildings. In addition, the energy certificate in Spain, as in the rest of the European Union, is legally mandatory when selling, renting, or constructing homes and commercial premises.

<strong>Why an energy certificate: the planet is warming</strong><br><br>

The Earth is warming due to the emission of greenhouse gases, including CO₂, which are released, among other sources, by the energy consumption of buildings. Homes and businesses play an important role: heating, cooling, and electricity consumption account for a significant share of total emissions.

The energy certificate was created to make these energy performances visible and comparable.  
In this way, owners and buyers can see how efficient a property is, and the adoption of energy-saving measures is encouraged, such as:
<br>• improved insulation of walls, roofs, and windows;<br>
• installation of solar panels;<br>
• or replacing heating systems with more efficient models, such as a heat pump.

Thanks to this, energy certificates contribute to European climate goals and to reducing CO₂ emissions in the building sector.

<strong>Why an energy certificate: it is a legal obligation</strong><br><br>

The energy certificate is mandatory in Spain under Royal Decree 390/2021, which implements European Directive 2010/31/EU on the energy performance of buildings.

To sell, rent, or construct a home or commercial premises, it is necessary to have a valid energy certificate.  
In addition, the certificate must appear in advertisements, both online (property portals, social networks) and offline.

The autonomous communities are responsible for supervising this obligation. Anyone who sells or rents a property without a certificate may face fines that vary by region and can reach several hundred euros.

<strong>Exceptions to the obligation</strong><br><br>

In Spain, as in other EU countries, there are some exceptions where it is not mandatory to have an energy certificate.  
A certificate is not required for:
<br>• buildings protected for their historical or architectural value;<br>
• buildings used for religious purposes (churches, mosques);<br>
• detached buildings of less than 50 m²;<br>
• agricultural or industrial buildings used exclusively for storage or processes;<br>
• temporary buildings with a planned duration of less than two years;<br>
• holiday homes occupied less than 4 months per year;<br>
• buildings with no energy consumption for heating or cooling (such as garages or warehouses).

The energy certificate is not only a legal obligation: it is also a key tool to promote more efficient energy use and a smaller environmental footprint.

Through <strong>epcspainhomes.com</strong> you can quickly find a certified technician who will assess your home or premises in accordance with Spanish regulations.
                            </p>
                        </div>
                    </div>

                    <br>

                    <div class="max-w-4xl mx-auto prose prose-lg">
                        <div class="bg-white p-8 rounded-lg shadow-sm">

<h2 id="what-is-energy-label" class="text-2xl text-blue-900 mb-4">What is an energy certificate?</h2>

<p class="mb-4">
To clearly compare the energy efficiency of buildings, the energy certificate was created.  
But what information does it contain exactly?

<strong>The rating: from A to G</strong><br><br>

The energy certificate shows letters from A to G that indicate how efficient a home or premises is:

<br><strong>A</strong> means very efficient  
<br><strong>G</strong> means very inefficient

The rating is determined based on several factors, such as:
<br>• insulation in walls, roofs, floors, and windows;<br>
• type of heating and cooling systems;<br>
• domestic hot water production;<br>
• presence of renewable energy, such as solar panels.

<strong>Improvement recommendations</strong><br><br>

In addition to the rating, the certificate includes specific recommendations to improve the energy efficiency of the property, for example:
<br>• improving insulation in the roof, façade, or floor;<br>
• replacing single-glazed windows with double or triple glazing;<br>
• installing solar panels or a heat pump.

These improvements can reduce consumption, increase comfort, and improve the value of the property.

<strong>From energy consumption to sustainability</strong><br><br>

The certificate shows how your home manages energy and how prepared it is for a sustainable future.  
A well-insulated property, with efficient electric heating or a heat pump, usually receives a better rating.

<strong>Who issues the certificate?</strong><br><br>

The energy certificate is prepared by an authorized certifying technician.  
This professional carries out a detailed inspection and records more than 150 characteristics of the building in the official software (CE3X, HULC…).

After the analysis, the certificate is registered with the competent regional authority.

A registered certificate is valid for 10 years.

Do you want to know if your home already has a valid certificate?  
At <strong>CEEcasasespaña.com</strong> we help you verify it and, if necessary, request a new one.
</p>

                        </div>
                    </div>

                    <br>

                    <div class="max-w-4xl mx-auto prose prose-lg">
                        <div class="bg-white p-8 rounded-lg shadow-sm">

<h2 id="what-is-the-benefit-of-an-energy-label" class="text-2xl text-blue-900 mb-4">What are the benefits of an energy certificate?</h2>

<p class="mb-4">
The energy certificate is mandatory when selling, renting, or constructing a property, but what advantages does it really offer?  
Here we summarize the main benefits.

<strong>Knowing the energy efficiency</strong><br><br>

Buyers or tenants can immediately know how much energy a home consumes.  
An efficient property offers more comfort, lower monthly costs, and fewer CO₂ emissions.

In addition, a good rating increases the value of the property.  
In Spain, buyers are increasingly willing to pay more for homes with an A, B, or C rating due to their lower energy costs.

<strong>Improvement advice</strong><br><br>

The certificate will include tailored improvement recommendations for the property, for example:
<br>• where to add insulation;<br>
• whether installing solar panels is cost-effective;<br>
• whether a heat pump could be a viable option.

<strong>Advantages when selling or renting</strong><br><br>

Properties with a high rating sell faster and at a better price.  
Tenants also increasingly value energy efficiency because it directly affects their monthly expenses.

<strong>Better financing</strong><br><br>

Many Spanish banks offer green mortgages or loans with special conditions for efficient homes or energy renovations.  
The official energy certificate serves as proof to access these benefits.

In summary, the energy certificate is much more than a legal obligation: it is a useful tool that provides value, savings, and sustainability.
</p>

                        </div>
                    </div>

                </main>

                <aside class="md:w-1/3 mt-6 md:mt-0">
                    <div class="bg-white p-8 rounded-lg shadow-sm">
                        <img src="/images/team/compare.webp" alt="Compare certified technicians" class="mb-4 w-full h-auto rounded-md object-cover" />
                        <p class="mb-4">Start comparing certified technicians in Spain.</p>
                        <p><a href="/cadastro/" data-route="/cadastro/" class="btn bg-green-500 text-white px-6 py-3 rounded-lg font-medium inline-block">Compare technicians</a></p>
                    </div>
                </aside>

            </div>
        </div>
    </section>
</div>

        `;
}
